import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Ticket, Sparkles, XCircle, CalendarClock, CreditCard } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  confirmed: "bg-blue-100 text-blue-700", pending_payment: "bg-amber-100 text-amber-700",
  cancelled: "bg-slate-100 text-slate-500", checked_in: "bg-emerald-100 text-emerald-700",
  checked_out: "bg-emerald-100 text-emerald-700", absent: "bg-red-100 text-red-700",
};

export function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [days, setDays] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [orders, setOrders] = useState({});
  const [partyOrders, setPartyOrders] = useState([]);
  const [payOrder, setPayOrder] = useState(null);
  const [resched, setResched] = useState(null); // booking being rescheduled
  const load = () => {
    api.get("/bookings").then((r) => setBookings(r.data));
    api.get("/payments/orders").then((r) => {
      setOrders(Object.fromEntries(r.data.map((o) => [o.id, o])));
      setPartyOrders(r.data.filter((o) => ["party", "party_balance", "late_fee"].includes(o.kind) && ["pending", "awaiting_confirmation"].includes(o.status)));
    }).catch(() => {});
  };
  useEffect(() => { load(); api.get("/public/calendar").then((r) => setDays(r.data)); api.get("/public/settings").then((r) => setDueDate(r.data.payment_due_date || "")); }, []);

  const cancel = async (id) => {
    try { await api.post(`/bookings/${id}/cancel`); toast.success("Booking cancelled. Eligible days become Adventure Credits."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doReschedule = async (newDate) => {
    try {
      await api.post(`/bookings/${resched.id}/reschedule`, { new_date: newDate });
      toast.success("Adventure moved! See you on the new day.");
      setResched(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDays = days.filter((d) => d.display === "available" && (!resched || d.date !== resched.date));

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><Ticket className="w-6 h-6 text-turquoise" /> My Bookings</h1>
      <p className="text-slate-500">Manage your explorers' adventure days.</p>
      <div className="mt-6 space-y-3">
        {bookings.map((b) => {
          const order = orders[b.payment_order_id];
          const awaiting = order?.status === "awaiting_confirmation";
          const canPay = b.status === "pending_payment" && b.amount > 0 && order?.status === "pending";
          return (
          <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`booking-${b.id}`}>
            <div>
              <p className="font-display font-bold text-navy">{new Date(b.date + "T12:00").toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" })}</p>
              <p className="text-sm text-slate-500">{b.child_name} · {b.package_type === "camp" ? (b.camp_name || "Camp") : `${b.package_type} pass`} {b.credit_used && "· credit used"}</p>
              {b.transport && b.transport !== "none" && (
                <p className="text-xs text-turquoise font-semibold mt-0.5" data-testid={`booking-transport-${b.id}`}>🚐 {b.transport === "both" ? "Pickup + Drop-off" : b.transport === "pickup" ? "School Pickup" : "After-School Drop-off"} · ${b.transport_cost} · {(b.transport_status || "requested").replace("_", " ")}</p>
              )}
              {canPay && order?.payment_due && (
                <p className={`text-xs font-semibold mt-0.5 ${order.overdue ? "text-red-600" : "text-coral"}`} data-testid={`booking-due-${b.id}`}>⏰ {order.overdue ? "Payment overdue — paid by" : "Payment pending — pay by"} {new Date(order.payment_due).toLocaleString("en", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} to secure this spot</p>
              )}
              {canPay && order?.booking_confirmed_at && <p className="text-xs text-emerald-600 font-semibold mt-0.5" data-testid={`booking-confirmed-${b.id}`}>✓ Booking confirmed — please pay with cash or WOI</p>}
              {awaiting && <p className="text-xs text-amber-600 font-semibold mt-0.5" data-testid={`booking-awaiting-${b.id}`}>✓ Payment submitted — awaiting confirmation from our team</p>}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`${STATUS[b.status] || "bg-slate-100 text-slate-500"} border-0`} data-testid={`booking-status-${b.id}`}>{b.status.replace("_", " ")}</Badge>
              {canPay && (
                <Button size="sm" className="bg-turquoise text-navy font-bold rounded-full" onClick={() => setPayOrder({ payment_order_id: b.payment_order_id, amount: order.amount, reference: order.reference, payment_due: order.payment_due, overdue: order.overdue })} data-testid={`pay-now-${b.id}`}><CreditCard className="w-4 h-4 mr-1" /> Complete Payment (${Number(order.amount).toFixed(2)})</Button>
              )}
              {["confirmed", "pending_payment"].includes(b.status) && (
                <>
                  {b.package_type !== "camp" && <Button size="sm" variant="ghost" className="text-turquoise" onClick={() => setResched(b)} data-testid={`reschedule-${b.id}`}><CalendarClock className="w-4 h-4 mr-1" /> Reschedule</Button>}
                  <Button size="sm" variant="ghost" className="text-coral" onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}><XCircle className="w-4 h-4 mr-1" /> Cancel</Button>
                </>
              )}
            </div>
          </div>
          );
        })}
        {bookings.length === 0 && <p className="text-slate-400">No bookings yet. Book your first adventure!</p>}
      </div>

      {partyOrders.length > 0 && (
        <div className="mt-8" data-testid="party-payments-section">
          <h2 className="font-display font-bold text-navy flex items-center gap-2"><CreditCard className="w-5 h-5 text-coral" /> Party & Event Payments</h2>
          <div className="mt-3 space-y-3">
            {partyOrders.map((o) => {
              const label = o.kind === "party_balance" ? "Party balance" : o.kind === "late_fee" ? `Late pickup fee (${o.meta?.minutes} min)` : "Party deposit / booking";
              const due = o.meta?.balance_due;
              return (
                <div key={o.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`party-order-${o.id}`}>
                  <div>
                    <p className="font-display font-bold text-navy">{label} · ${Number(o.amount).toFixed(0)}</p>
                    <p className="text-sm text-slate-500">{o.meta?.date ? `${o.kind === "late_fee" ? "Pickup" : "Event"} ${new Date(o.meta.date + "T12:00").toLocaleDateString("en", { month: "long", day: "numeric" })}` : ""} <span className="font-mono text-turquoise text-xs">{o.reference}</span></p>
                    {due && o.status === "pending" && <p className="text-xs text-coral font-semibold mt-0.5" data-testid={`party-order-due-${o.id}`}>⏰ Balance due by {new Date(due + "T12:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })}</p>}
                    {o.status === "awaiting_confirmation" && <p className="text-xs text-amber-600 font-semibold mt-0.5">✓ Payment submitted — awaiting confirmation</p>}
                  </div>
                  {o.status === "pending" && (
                    <Button size="sm" className="bg-coral text-white font-bold rounded-full" onClick={() => setPayOrder({ payment_order_id: o.id, amount: o.amount, reference: o.reference })} data-testid={`party-pay-${o.id}`}><CreditCard className="w-4 h-4 mr-1" /> Pay ${Number(o.amount).toFixed(0)}</Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!resched} onOpenChange={(o) => !o && setResched(null)}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Move this adventure</DialogTitle>
            <DialogDescription>Pick a new open day. Your spot moves in one tap — no need to cancel.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 max-h-72 overflow-y-auto">
            {openDays.map((d) => (
              <button key={d.date} onClick={() => doReschedule(d.date)} data-testid={`resched-day-${d.date}`}
                className="px-3 py-2 rounded-lg text-xs font-semibold border border-slate-200 text-navy hover:border-turquoise hover:bg-turquoise/10 transition-all">
                {new Date(d.date + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}
              </button>
            ))}
            {openDays.length === 0 && <p className="text-slate-400 text-sm">No other open days available right now.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <PaymentDialog order={payOrder} open={!!payOrder} onClose={() => { setPayOrder(null); load(); }} onDone={load} />
    </div>
  );
}

export function Credits() {
  const [credits, setCredits] = useState([]);
  useEffect(() => { api.get("/credits").then((r) => setCredits(r.data)); }, []);
  const avail = credits.filter((c) => c.status === "available");

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><Sparkles className="w-6 h-6 text-gold" /> Adventure Credits</h1>
      <p className="text-slate-500">Plans change. Your adventure doesn't disappear.</p>
      <div className="bg-gradient-to-br from-navy to-ocean text-white rounded-2xl p-6 mt-6 text-center" data-testid="credits-balance">
        <p className="font-display font-black text-5xl text-gold">{avail.length}</p>
        <p className="text-white/70">Available Adventure Credits</p>
      </div>
      <div className="mt-6 space-y-3">
        {credits.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`credit-${c.id}`}>
            <div><p className="font-semibold text-navy">{c.child_name}</p><p className="text-sm text-slate-500">{c.reason}</p></div>
            <Badge className={`border-0 ${c.status === "available" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</Badge>
          </div>
        ))}
        {credits.length === 0 && <p className="text-slate-400">No credits yet.</p>}
      </div>
    </div>
  );
}
